import React from 'react';
import { Link } from 'react-router-dom';
import '../HomePage.css';
import './SafetyDataGuidePage.css';
import googlePlayImage from '../assets/images/google-play.png';
import appleStoreImage from '../assets/images/apple-store.png';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { onAppStoreClick } from '../utils/metaPixel';

const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL ?? 'https://play.google.com/store/apps/details?id=com.safescope.app&utm_source=na_Med';
const APP_STORE_URL = 'https://apps.apple.com/us/app/consilium-safety/id6760517718';

const methodSteps = [
  {
    step: '1',
    title: 'Find the source',
    body: 'Look for the agency, dataset, report date, or original record behind the claim.',
  },
  {
    step: '2',
    title: 'Check the time window',
    body: 'A quiet recent week may be a reporting lag. A loud day may still be normal over a month.',
  },
  {
    step: '3',
    title: 'Compare to baseline',
    body: 'Ask whether the count is unusual for that exact area, not whether the headline sounds alarming.',
  },
  {
    step: '4',
    title: 'Look for the caveat',
    body: 'Reported incidents, category definitions, and agency practices all shape what the number can mean.',
  },
];

const sourceCards = [
  {
    title: 'A floor, not a count',
    body: 'Reported-incident data tracks what was reported, not everything that happened. Read any number as "at least this many."',
  },
  {
    title: 'A few weeks behind',
    body: 'Records finalize after review. A low recent count can mean the data is incomplete, not that risk disappeared.',
  },
  {
    title: 'Categories vary by agency',
    body: 'One city may split theft into several categories while another groups them together. Compare like with like.',
  },
];

const rumorSteps = [
  '1 report, dated and sourced',
  'Shared with a worried caption',
  'Reposted with more alarm',
  '"Crime wave" with no new data',
];

const AppDownloadButtons: React.FC<{ placement: string }> = ({ placement }) => (
  <div className="store-buttons safety-data-store-buttons" aria-label="Download Consilium">
    <a
      href={GOOGLE_PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="store-button google"
      aria-label="Get Consilium on Google Play"
      onClick={onAppStoreClick('android', { placement })}
    >
      <img src={googlePlayImage} alt="Get it on Google Play" className="store-icon" />
    </a>
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="store-button apple"
      aria-label="Download Consilium on the App Store"
      onClick={onAppStoreClick('ios', { placement })}
    >
      <img src={appleStoreImage} alt="Download on the App Store" className="store-icon" />
    </a>
  </div>
);

const SafetyDataGuidePage: React.FC = () => (
  <div className="safety-data-page-wrapper">
    <Header downloadHref="#download-consilium" />

    <main className="safety-data-page" id="top">
      <header className="safety-data-hero">
        <div className="safety-data-hero-copy">
          <div className="safety-data-eyebrow">Consilium Civic Intelligence</div>
          <h1>How to read your neighborhood's public safety data</h1>
          <p>
            A headline, a neighbor's post, and a number on a map can all feel urgent. This guide shows you how to check
            what the data actually says, using the same questions Consilium applies before turning information into an
            alert.
          </p>
          <div className="safety-data-hero-actions">
            <a href="#method" className="safety-data-primary-link">Use the ten-minute method</a>
            <a href="#download-consilium" className="safety-data-secondary-link">Get Consilium</a>
          </div>
        </div>
        <aside className="safety-data-region-card" aria-label="Sample neighborhood data card">
          <div className="safety-data-region-header">
            <span>[Neighborhood], [City]</span>
            <small>Updated today</small>
          </div>
          <div className="safety-data-mini-chart" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="safety-data-region-bars">
            <div><span>Property</span><strong style={{ width: '55%' }} /></div>
            <div><span>Theft</span><strong style={{ width: '33%' }} /></div>
            <div><span>Other</span><strong style={{ width: '18%' }} /></div>
          </div>
          <p>Within baseline for this area</p>
          <small>Scoped to this block &middot; same vetting standard as this guide</small>
        </aside>
      </header>

      <section className="safety-data-section">
        <div className="safety-data-eyebrow">01 - The Word "Breaking" Is Not a Data Point</div>
        <h2>Urgency is not the same thing as significance.</h2>
        <p>
          "Breaking" describes how recently something was published. It does not tell you whether the event is unusual,
          whether your neighborhood changed, or whether there is anything you can do. A single incident can sound like a
          trend when it is separated from its baseline.
        </p>

        <div className="safety-data-split-card">
          <div className="safety-data-headline-card">
            <span>Breaking - 3 min ago</span>
            <h3>"Crime Wave Hits Downtown," Residents Say</h3>
            <p>The headline gives urgency with no baseline attached.</p>
          </div>
          <div className="safety-data-baseline-card">
            <span>12-month trend</span>
            <div className="safety-data-trend-line" aria-hidden="true" />
            <h3>Within normal range for this area</h3>
            <p>The data shows one incident, not a pattern shift.</p>
          </div>
        </div>

        <div className="safety-data-study-note">
          A 2023 study found identical stories were rated as more sensational and less trustworthy once labeled
          "breaking." The facts had not changed.
        </div>
      </section>

      <section className="safety-data-section">
        <div className="safety-data-eyebrow">How A Rumor Spreads Without New Information</div>
        <h2>The framing can escalate faster than the facts.</h2>
        <p>
          A single report can pick up alarm at every share: a neighbor mentions it, someone reposts it with a worried
          caption, then someone else screenshots the post. By the time it feels like it is everywhere, the only thing
          that may have grown is the framing.
        </p>
        <div className="safety-data-rumor-chain">
          {rumorSteps.map((step, index) => (
            <React.Fragment key={step}>
              <article>
                <span>{index + 1}</span>
                <p>{step}</p>
              </article>
              {index < rumorSteps.length - 1 && <div className="safety-data-chain-arrow" aria-hidden="true">-</div>}
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="safety-data-section">
        <div className="safety-data-eyebrow">02 - Where The Numbers Actually Come From</div>
        <h2>Traceable, but imperfect by design.</h2>
        <p>
          Public safety data typically traces back to a local police records system, a sheriff's office dataset, or a
          state-level reporting program that rolls records up to broader systems like NIBRS. If a claim names no source,
          that is worth noticing.
        </p>
        <div className="safety-data-source-grid">
          {sourceCards.map(({ title, body }) => (
            <article key={title}>
              <span aria-hidden="true" />
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="safety-data-section" id="method">
        <div className="safety-data-eyebrow">03 - A Ten-Minute Method You Can Use On Any Claim</div>
        <h2>Read a number without being misled by it.</h2>
        <p>
          One incident is not a trend. What matters is the count over time, compared to a baseline, with the caveats
          attached.
        </p>
        <div className="safety-data-method-grid">
          {methodSteps.map(({ step, title, body }) => (
            <article key={step}>
              <span>{step}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="safety-data-consilium-card">
        <div>
          <div className="safety-data-eyebrow">04 - What A Region Look-Up Actually Shows</div>
          <h2>The same check, already done for your neighborhood.</h2>
          <p>
            Consilium is built to show local information as vetted, dated, and compared to the area's own baseline.
            The same standard applies across crime, public health, and environmental data.
          </p>
        </div>
        <ul>
          <li>Vetted records, not anonymous alarm.</li>
          <li>Time windows and update dates shown clearly.</li>
          <li>Baseline comparison before calling something unusual.</li>
          <li>Plain language for what changed and why it matters.</li>
        </ul>
      </section>

      <section className="safety-data-callout">
        <p>
          The same vetting, time-window, and baseline questions work outside of crime. They are also how to read local
          public health signals, disease reporting, air quality, wildfire smoke, and other environmental conditions
          without being misled by either a scary headline or a falsely reassuring one.
        </p>
      </section>

      <section className="safety-data-app-cta" id="download-consilium">
        <div>
          <div className="safety-data-eyebrow">Download Consilium</div>
          <h2>Check your own neighborhood in about a minute.</h2>
          <p>
            Get vetted, dated, baseline-compared intelligence for the places you care about, without running the
            ten-minute check yourself every time.
          </p>
        </div>
        <AppDownloadButtons placement="safety-data-guide" />
      </section>

      <section className="safety-data-companion">
        <h2>Companion Guide</h2>
        <p>
          For related context, read Consilium's companion pieces:
          <Link to="/health-environmental-data"> local health and environmental data</Link> and
          <Link to="/breaking-news-problem"> the breaking-news problem</Link>.
        </p>
      </section>
    </main>

    <Footer />
  </div>
);

export default SafetyDataGuidePage;
